import type { AuthChangeEvent, Session } from '@supabase/supabase-js'

export interface AuthClient {
  signOut: (options: { scope: 'local' }) => Promise<{ error: unknown | null }>
  getUser: () => Promise<{ data: { user: unknown | null }; error: unknown | null }>
  _removeSession: () => Promise<void>
  _notifyAllSubscribers: (event: AuthChangeEvent, session: Session | null) => Promise<void>
}

export type FunctionSessionProbe = 'not-applicable' | 'valid' | 'invalid' | 'unavailable'

const statusOf = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null || !('status' in error)) return undefined
  return typeof error.status === 'number' ? error.status : undefined
}

const functionStatusOf = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null || !('context' in error)) return undefined
  const { context } = error
  if (typeof context !== 'object' || context === null || !('status' in context)) return undefined
  return typeof context.status === 'number' ? context.status : undefined
}

export const forceLocalSignOut = async (auth: AuthClient): Promise<void> => {
  // Supabase does not expose a public offline-only cleanup operation. Keep the
  // private fallback confined here and covered by tests.
  // eslint-disable-next-line no-underscore-dangle
  await auth._removeSession()
  // eslint-disable-next-line no-underscore-dangle
  await auth._notifyAllSubscribers('SIGNED_OUT', null)
}

export const signOutLocally = async (auth: AuthClient): Promise<unknown | null> => {
  let remoteError: unknown | null = null
  try {
    const { error } = await auth.signOut({ scope: 'local' })
    remoteError = error
  } catch (error) {
    remoteError = error
  }

  if (remoteError) await forceLocalSignOut(auth)

  return remoteError
}

/** Distinguishes a dead session from a real permission error without parsing messages. */
export const probeFunctionSession = async (
  auth: AuthClient,
  functionError: unknown
): Promise<FunctionSessionProbe> => {
  const functionStatus = functionStatusOf(functionError)
  if (functionStatus === 401) return 'invalid'
  if (functionStatus !== 403) return 'not-applicable'

  try {
    const { data, error } = await auth.getUser()
    if (!error) return data.user ? 'valid' : 'invalid'

    const authStatus = statusOf(error)
    return authStatus !== undefined && authStatus >= 400 && authStatus < 500 ? 'invalid' : 'unavailable'
  } catch {
    return 'unavailable'
  }
}
